import { useState } from "react";
import axios from "axios";
import { API } from "../../App";

const REG_TYPES = ["LIRSS Only", "LIBSS Only", "LIRSS + LIBSS Combo"];
const ATTENDEE_TYPES = ["Complimentary", "Faculty", "Sponsor", "Trade", "Delegate"];
const DELEGATE_TYPES = ["Consultant", "Trainee", "Nurse"];
const LIBSS_DELEGATE_TYPES = ["Consultant (Doctor)", "Trainee (Doctor)", "Allied Health Professional"];
const TITLES = ["Mr", "Mrs", "Ms", "Dr", "Prof"];

const initialForm = {
  title: "Dr", firstName: "", lastName: "", email: "", hospital: "", country: "",
  registrationType: "LIRSS Only", attendeeType: "Complimentary", delegateType: "",
  attendanceDays: "", attendanceDate: ""
};

const ManualRegistrationForm = () => {
  const [form, setForm] = useState(initialForm);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const isLibss = form.registrationType === "LIBSS Only";
  const isDelegate = form.attendeeType === "Delegate";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const token = localStorage.getItem("adminToken");
      const payload = { ...form };
      if (!isLibss) {
        payload.attendanceDays = null;
        payload.attendanceDate = null;
      } else if (payload.attendanceDays !== "One Day") {
        payload.attendanceDate = null;
      }
      if (!isDelegate) payload.delegateType = null;
      const res = await axios.post(`${API}/registrations/admin/manual`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: "success", text: `Created — Hub link: /hub/${res.data.data.hubToken}` });
      setForm(initialForm);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const text = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
        : (detail || "Failed to create registration");
      setMessage({ type: "error", text });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-slate-800/60 rounded-xl border border-slate-700 p-6 space-y-4" data-testid="manual-registration-form">
      <h2 className="text-lg font-semibold text-white">Add Manual / Complimentary Registration</h2>
      {message && (
        <div className={`px-3 py-2 rounded text-sm ${message.type === "success" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`} data-testid="manual-form-message">
          {message.text}
        </div>
      )}
      <div className="grid grid-cols-4 gap-3">
        <select name="title" value={form.title} onChange={handleChange} className="bg-slate-700 border border-slate-600 rounded px-2 py-2 text-white text-sm" data-testid="manual-title">
          {TITLES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="First name" required className="bg-slate-700 border border-slate-600 rounded px-2 py-2 text-white text-sm col-span-1" data-testid="manual-firstname" />
        <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last name" required className="bg-slate-700 border border-slate-600 rounded px-2 py-2 text-white text-sm col-span-2" data-testid="manual-lastname" />
      </div>
      <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email" required className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm" data-testid="manual-email" />
      <div className="grid grid-cols-2 gap-3">
        <input name="hospital" value={form.hospital} onChange={handleChange} placeholder="Hospital / Institution" required className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm" data-testid="manual-hospital" />
        <input name="country" value={form.country} onChange={handleChange} placeholder="Country" required className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm" data-testid="manual-country" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <select name="registrationType" value={form.registrationType} onChange={handleChange} className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm" data-testid="manual-registration-type">
          {REG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select name="attendeeType" value={form.attendeeType} onChange={handleChange} className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm" data-testid="manual-attendee-type">
          {ATTENDEE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      {isDelegate && (
        <select name="delegateType" value={form.delegateType} onChange={handleChange} required className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm" data-testid="manual-delegate-type">
          <option value="">Select delegate category</option>
          {(isLibss ? LIBSS_DELEGATE_TYPES : DELEGATE_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      )}
      {isLibss && (
        <div className="grid grid-cols-2 gap-3">
          <select name="attendanceDays" value={form.attendanceDays} onChange={handleChange} required className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm" data-testid="manual-attendance-days">
            <option value="">Days attending</option>
            <option value="One Day">One Day</option>
            <option value="Two Days">Two Days</option>
          </select>
          {form.attendanceDays === "One Day" && (
            <select name="attendanceDate" value={form.attendanceDate} onChange={handleChange} required className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm" data-testid="manual-attendance-date">
              <option value="">Which date</option>
              <option value="November 26, 2026">November 26, 2026</option>
              <option value="November 27, 2026">November 27, 2026</option>
            </select>
          )}
        </div>
      )}
      <button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2 rounded-lg text-sm disabled:opacity-50" data-testid="manual-submit-btn">
        {loading ? "Creating..." : "Create Registration"}
      </button>
    </form>
  );
};

export default ManualRegistrationForm;
